<template>
  <div
    class="flex flex-col w-full h-full bg-[#111a27] text-slate-200 rounded-[22px] shadow overflow-hidden border border-white/10"
  >
    <!-- 채팅 헤더 -->
    <div class="border-b border-white/10 bg-[#142235] px-3 py-2.5 sm:px-4 sm:py-3">
      <div class="flex items-center justify-end gap-2 sm:justify-between sm:gap-3">
        <span class="hidden font-black text-[#1ec8ff] sm:inline">채팅방</span>
        <button
          @click="openChatRooms"
          class="rounded-full border border-[#1ec8ff]/40 p-2 text-[#1ec8ff] hover:bg-[#1ec8ff] hover:text-[#111a27] sm:px-3 sm:py-1.5 sm:text-xs sm:font-black"
          aria-label="채팅방 목록 가기"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4 sm:hidden"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
          <span class="hidden sm:inline">채팅방 목록 가기</span>
        </button>
      </div>
    </div>
    <!-- 메시지 영역 -->
    <div
      ref="messageContainer"
      class="flex-1 overflow-y-auto p-3 space-y-3 lg:overflow-hidden sm:p-4 sm:space-y-4"
    >
      <div
        v-for="message in messages"
        :key="message.id"
        class="flex"
        :class="message.senderId === currentUser?._id ? 'justify-end' : 'justify-start'"
      >
        <div
          class="max-w-[85%] rounded-lg px-3 py-2 lg:max-w-md sm:max-w-xs sm:px-4"
          :class="
            message.senderId === currentUser?._id
              ? 'bg-[#1ec8ff] text-[#111a27] text-right font-bold'
              : 'bg-white/5 text-slate-100 border border-white/10 text-left'
          "
        >
          <!-- 닉네임: 타인만 표시 (좌측, 작게, 배경 없음) -->
          <div
            v-if="message.senderId !== currentUser?._id"
            class="mb-1 text-left text-xs font-medium text-[#1ec8ff]"
            :class="{ 'cursor-pointer hover:text-[#7de3ff]': message.senderId }"
            @click="openProfilePopup(message.senderId)"
          >
            {{ message.senderName }}
          </div>
          <!-- 메시지 내용 -->
          <div
            class="text-sm"
            :class="message.senderId === currentUser?._id ? 'text-right' : 'text-left'"
          >
            {{ message.message }}
          </div>
        </div>
      </div>
    </div>
    <!-- 메시지 입력 영역 -->
    <div class="border-t border-white/10 bg-[#142235] p-2.5 sm:p-3">
      <div v-if="currentUser" class="flex gap-2">
        <input
          v-model="sendMessage"
          @keyup.enter="addMessage"
          type="text"
          placeholder="메시지를 입력하세요..."
          class="flex-1 rounded-xl border border-white/10 bg-black/30 px-3 py-2.5 text-sm text-slate-100 placeholder-slate-400 focus:outline-none focus:ring focus:ring-[#1ec8ff]/30 sm:text-base"
        />
        <button
          @click="addMessage"
          :disabled="sendMessage.length === 0"
          class="rounded-xl bg-[#1ec8ff] px-4 py-2.5 text-sm font-black text-[#111a27] hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50 sm:px-5 sm:text-base"
        >
          전송
        </button>
      </div>
      <div v-else class="text-center">
        <button
          @click="$router.push({ name: 'login' })"
          class="px-5 py-2 rounded-xl bg-[#1ec8ff] text-[#111a27] font-black hover:brightness-110"
        >
          로그인 후 채팅하기
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ChatView',
  data() {
    return {
      sendMessage: '',
    };
  },
  computed: {
    messages() {
      return this.$store.state.messages;
    },
    currentUser() {
      return this.$store.state.currentUser;
    },
  },
  mounted() {
    // 최근 메시지 25개 초기 로드
    const base = process.env.VUE_APP_API_URL || 'http://localhost:3000';
    fetch(`${base}/chat/messages?limit=25`)
      .then((r) => r.json())
      .then((data) => {
        const list = (data && data.messages) || [];
        this.$store.commit('setMessages', list);
        this.$nextTick(() => this.scrollToBottom());
      })
      .catch(() => {});
    // 소켓 연결 및 메시지 리스너 설정
    this.$store.commit('onMessage');
    // 스크롤을 맨 아래로 이동 (보조)
    this.$nextTick(() => {
      this.scrollToBottom();
    });
  },
  updated() {
    // 메시지가 추가될 때마다 스크롤을 맨 아래로 이동
    this.$nextTick(() => {
      this.scrollToBottom();
    });
  },
  methods: {
    openProfilePopup(userId) {
      if (!userId) return;
      const route = this.$router.resolve({ name: 'profile', query: { userId } });
      const profileUrl = new URL(route.href, window.location.origin).href;
      const width = 560;
      const height = 760;
      const left = Math.max(0, window.screenX + (window.outerWidth - width) / 2);
      const top = Math.max(0, window.screenY + (window.outerHeight - height) / 2);
      const popup = window.open(
        profileUrl,
        `atozProfile${userId}`,
        [
          'popup=yes',
          `width=${width}`,
          `height=${height}`,
          `left=${Math.round(left)}`,
          `top=${Math.round(top)}`,
          'resizable=yes',
          'scrollbars=yes',
          'noopener',
          'noreferrer',
        ].join(','),
      );
      if (popup) {
        popup.opener = null;
        popup.focus();
      }
    },
    openChatRooms() {
      const route = this.$router.resolve({ name: 'chatRooms' });
      const width = Math.max(1180, Math.floor(window.screen.availWidth * 0.98));
      const height = Math.max(820, Math.floor(window.screen.availHeight * 0.96));
      const left = Math.max(0, window.screen.availLeft || 0);
      const top = Math.max(0, window.screen.availTop || 0);
      const features = [
        'popup=yes',
        `width=${width}`,
        `height=${height}`,
        `left=${Math.round(left)}`,
        `top=${Math.round(top)}`,
        'resizable=yes',
        'scrollbars=yes',
        'noopener',
        'noreferrer',
      ].join(',');
      const popupUrl = new URL(route.href, window.location.origin).href;
      const popup = window.open(popupUrl, 'atozChatRooms', features);
      if (popup) {
        popup.opener = null;
        popup.focus();
      }
    },
    addMessage() {
      if (!this.sendMessage.trim()) return;
      this.$store.commit('sendMessage', this.sendMessage);
      this.sendMessage = '';
    },
    formatTime(timestamp) {
      const date = new Date(timestamp);
      const now = new Date();
      const diffInHours = (now - date) / (1000 * 60 * 60);
      if (diffInHours < 24) {
        return date.toLocaleTimeString('ko-KR', {
          hour: '2-digit',
          minute: '2-digit',
        });
      } else {
        return (
          date.toLocaleDateString('ko-KR', {
            month: '2-digit',
            day: '2-digit',
          }) +
          ' ' +
          date.toLocaleTimeString('ko-KR', {
            hour: '2-digit',
            minute: '2-digit',
          })
        );
      }
    },
    scrollToBottom() {
      if (this.$refs.messageContainer) {
        this.$refs.messageContainer.scrollTop = this.$refs.messageContainer.scrollHeight;
      }
    },
  },
};
</script>

<style scoped>
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}
.overflow-y-auto::-webkit-scrollbar-track {
  background: #111a27;
}
.overflow-y-auto::-webkit-scrollbar-thumb {
  background: rgba(30, 200, 255, 0.58);
  border-radius: 999px;
}
.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: rgba(30, 200, 255, 0.86);
}
</style>
